// Cliente de los endpoints Java de permisos / colaboradores.
// Mismo patron que los otros *back.js: token via getUser(),
// RUTA_JAVA por env, handleAuthenticatedRequest para el 401.

import { getUser } from "$lib/userstorage/usersotrage"
import { handleAuthenticatedRequest, jsonOrThrow } from "../errores/erroresback"

let ruta_java = import.meta.env.VITE_RUTA_JAVA_SERVER;
let ruta_local_java = import.meta.env.VITE_RUTA_LOCAL_JAVA_SERVER;
let bd_local = import.meta.env.VITE_LOCAL_BD=="si";
let RUTA_JAVA =bd_local? ruta_local_java:ruta_java;

function authHeaders() {
    const user = getUser()
    return {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${user.token}`,
    }
}

// `jsonOrThrow` vive en erroresback.js: arroja un Error con `.status` para que
// el caller alto (screen) pueda distinguir "no autorizado" de otros errores.

// --- Cache offline de permisos (localStorage) ---
// Los permisos gatean los botones en cada pantalla; offline el fetch tira
// `Failed to fetch` y dejaría los botones deshabilitados. Cacheamos el último
// valor conocido y lo servimos cuando la red está caída, sin enmascarar un 401/
// 403 (sesión/autorización) ni un 4xx/5xx real, que sí deben propagarse.
function readJsonCache(key) {
    try {
        const raw = localStorage.getItem(key)
        return raw ? JSON.parse(raw) : null
    } catch (e) {
        return null
    }
}
function writeJsonCache(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value))
    } catch (e) {
        // storage lleno o no disponible: ignorar (el cache es best-effort)
    }
}
function permisosCacheKey(estId, userId) {
    return `cf_permisos_${estId}_${userId}`
}
const CATALOG_CACHE_KEY = "cf_permisos_catalog"
// true = error "real" (sesión expirada o HTTP 4xx/5xx): propagar, NO servir cache.
// false = red caída / offline (fetch rechazado): podemos caer al cache.
function isRealError(err) {
    return !!err && (err.message === "SESIÓN_EXPIRADA" || typeof err.status === "number")
}

// Lista los colaboradores del establecimiento (con su rol).
// Devuelve [{userId, email, firstName, lastName, roleId, roleCode, roleName}].
export async function listColaboradores(estId) {
    const url = `${RUTA_JAVA}establishments/${estId}/collaborators`
    const res = await handleAuthenticatedRequest(url, { headers: authHeaders() })
    return await jsonOrThrow(res, "permisos.listColaboradores")
}

// Catalogo completo de permisos (resource:action). No cambia entre
// sesiones, lo cacheamos en memoria del modulo + en localStorage (offline).
let _catalogCache = null
export async function loadCatalog() {
    if (_catalogCache) return _catalogCache
    const url = `${RUTA_JAVA}permissions`
    try {
        const res = await handleAuthenticatedRequest(url, { headers: authHeaders() })
        _catalogCache = await jsonOrThrow(res, "permisos.loadCatalog")
        writeJsonCache(CATALOG_CACHE_KEY, _catalogCache)
        return _catalogCache
    } catch (err) {
        if (isRealError(err)) throw err
        // Red caída: servir el último catálogo conocido si existe.
        const cached = readJsonCache(CATALOG_CACHE_KEY)
        if (cached) {
            _catalogCache = cached
            return cached
        }
        throw err
    }
}

// Códigos "RECURSO:ACCION" de permisos efectivos de un usuario en un
// establecimiento (lo que espera hasPermission). Devuelve string[].
// Cachea por (estId, userId) para uso offline.
export async function getColabPermisos(estId, userId) {
    const url = `${RUTA_JAVA}establishments/${estId}/users/${userId}/permissions`
    try {
        const res = await handleAuthenticatedRequest(url, { headers: authHeaders() })
        const permisos = await jsonOrThrow(res, "getColabPermisos")
        writeJsonCache(permisosCacheKey(estId, userId), permisos)
        return permisos
    } catch (err) {
        if (isRealError(err)) throw err
        // Red caída (offline): servir los permisos cacheados de la última
        // sesión online, así los botones siguen habilitados sin red.
        const cached = readJsonCache(permisosCacheKey(estId, userId))
        if (cached) return cached
        throw err
    }
}

// Permisos efectivos ("RESOURCE:ACTION") del usuario LOGUEADO en el
// establecimiento al que esta scopeado su JWT. Sirve para ocultar acciones
// que el back va a rechazar con 403.
//
// Se usa `/api/auth/me` y no `getColabPermisos` por dos motivos: devuelve los
// strings ya armados (getColabPermisos devuelve ids, que hay que cruzar contra
// el catalogo) y sale de las authorities reales del token, asi que para un ADM
// refleja el fast-path del JwtAuthFilter en vez de sus filas de UEP.
const MIS_PERMISOS_KEY = "cf_mis_permisos"

// Clave del storage proxy que leen los gates de las pantallas
// (`createStorageProxy("detallepermisos2", ...)`).
const GATES_KEY = "detallepermisos2"

export async function getMisPermisos(estId) {
    const url = `${RUTA_JAVA}auth/me`
    try {
        const res = await handleAuthenticatedRequest(url, { headers: authHeaders() })
        const perfil = await jsonOrThrow(res, "getMisPermisos")
        const permisos = perfil.permissions || []
        writeJsonCache(MIS_PERMISOS_KEY, { estId, permisos })
        return permisos
    } catch (err) {
        if (isRealError(err)) throw err
        // Red caída: servir el ultimo set conocido, pero solo si es del
        // mismo establecimiento (los permisos son por scope).
        const cached = readJsonCache(MIS_PERMISOS_KEY)
        if (cached && cached.estId == estId) return cached.permisos
        throw err
    }
}

// Deja los permisos en el storage que leen los gates. Lo llama el layout al
// arrancar: sin esto, una pantalla de detalle abierta por deep-link o F5 (que
// solo hace proxyPermisos.load(), sin fetchear) encontraria la lista vacia y
// bloquearia todas las acciones. Best-effort: si falla, cada pantalla que si
// fetchea lo resuelve por su cuenta.
export async function warmMisPermisos(estId) {
    const permisos = await getMisPermisos(estId)
    try {
        const prev = JSON.parse(localStorage.getItem(GATES_KEY) || "{}")
        localStorage.setItem(GATES_KEY, JSON.stringify({ ...prev, permisos }))
    } catch (e) {
        // storage lleno o no disponible: los gates caen al fetch por pantalla
    }
    return permisos
}

// Asigna un rol al usuario en el establecimiento. Borra todos sus
// permisos previos y copia los del template del rol (reset completo).
export async function assignRole(estId, userId, roleId) {
    const url = `${RUTA_JAVA}establishments/${estId}/users/${userId}/permissions/role/${roleId}`
    const res = await handleAuthenticatedRequest(url, {
        method: "PUT",
        headers: authHeaders(),
    })
    if (!res.ok) throw new Error(`assignRole failed: ${res.status}`)
}

// Otorga un permiso puntual. No-op si el usuario ya lo tiene.
export async function grantPermiso(estId, userId, permId) {
    const url = `${RUTA_JAVA}establishments/${estId}/users/${userId}/permissions/${permId}`
    const res = await handleAuthenticatedRequest(url, {
        method: "POST",
        headers: authHeaders(),
    })
    if (!res.ok) throw new Error(`grantPermiso failed: ${res.status}`)
}

// Quita un permiso puntual. No-op si el usuario no lo tenia.
export async function revokePermiso(estId, userId, permId) {
    const url = `${RUTA_JAVA}establishments/${estId}/users/${userId}/permissions/${permId}`
    const res = await handleAuthenticatedRequest(url, {
        method: "DELETE",
        headers: authHeaders(),
    })
    if (!res.ok) throw new Error(`revokePermiso failed: ${res.status}`)
}

// Lista los roles del sistema. Lo usa el screen para resolver
// roleCode (ADM/ENC/OPE) → roleId que espera assignRole. Los IDs no
// son estables entre DBs, asi que no los hardcodeamos.
export async function getRoles() {
    const url = `${RUTA_JAVA}roles/all`
    const res = await handleAuthenticatedRequest(url, { headers: authHeaders() })
    return await jsonOrThrow(res, "permisos.getRoles")
}
// Gate de UI. `permiso` es un string "RESOURCE:ACTION" y `lista` es lo que
// devuelve `getMisPermisos`.
//
// Antes esto recibia ids numericos del catalogo y estaba roto: los ids salen
// del AUTO_INCREMENT del seed, cambian por entorno, y el mismo numero se usaba
// para dos cosas distintas (el 21 era "ver rodeos" en una pantalla y "ver
// observaciones" en otra, el 4 era eliminar pesajes / dar de baja / eliminar
// animales). El string es estable entre bases.
export function hasPermission(lista, permiso){
    if (!Array.isArray(lista)) return false
    return lista.includes(permiso)
}